import { api, ApiError, type SignInResponse } from '../api';
import { esc, html, toast } from '../ui';

// The FlyPack mark, as on www.flypack.net.
const LOGO = html`<svg width="30" height="30" viewBox="0 0 64 64" fill="#7cc7ef" aria-hidden="true"><path d="M0 33V6A6 6 0 0 1 6 0H29V20.3Z"/><path d="M64 33V6A6 6 0 0 0 58 0H35V20.3Z"/><path d="M0 40L29 27.3V48.3L18 53V58L29 53.3V64H6A6 6 0 0 1 0 58Z"/><path d="M64 40L35 27.3V48.3L46 53V58L35 53.3V64H58A6 6 0 0 0 64 58Z"/></svg>`;

const GOOGLE_G = html`<svg width="17" height="17" viewBox="0 0 24 24" aria-hidden="true">
  <path fill="currentColor" opacity=".85" d="M21.6 12.2c0-.7-.06-1.35-.18-2H12v3.8h5.4a4.6 4.6 0 0 1-2 3v2.5h3.2c1.9-1.75 3-4.32 3-7.3Z"/>
  <path fill="currentColor" opacity=".65" d="M12 22c2.7 0 4.97-.9 6.6-2.43l-3.2-2.5c-.9.6-2.05.95-3.4.95-2.6 0-4.8-1.76-5.6-4.12H3.1v2.6A10 10 0 0 0 12 22Z"/>
  <path fill="currentColor" opacity=".45" d="M6.4 13.9a6 6 0 0 1 0-3.8V7.5H3.1a10 10 0 0 0 0 9l3.3-2.6Z"/>
  <path fill="currentColor" opacity=".8" d="M12 5.98c1.47 0 2.79.5 3.83 1.5l2.84-2.84C16.96 2.99 14.7 2 12 2A10 10 0 0 0 3.1 7.5l3.3 2.6C7.2 7.74 9.4 5.98 12 5.98Z"/>
</svg>`;

const artPanel = html`
  <div class="auth-art">
    <div style="display:flex;align-items:center;gap:12px">
      ${LOGO}
      <div style="display:flex;flex-direction:column;gap:4px">
        <span style="font-family:var(--display);font-size:24px;font-weight:700;line-height:.92">FlyPack</span>
        <span style="font-family:var(--mono);font-size:9px;font-weight:500;letter-spacing:.24em;text-transform:uppercase;color:#7cc7ef">CAMO Software</span>
      </div>
    </div>
    <div>
      <h2>Continuing airworthiness for small fleets, without the spreadsheet.</h2>
      <p class="lead">
        Airworthiness programmes, AD applicability, component lives, tech log and airworthiness
        review — for Part-ML, Part-M Subpart G and third-country registries on the same field.
      </p>
      <div class="regstrip">
        <span>PART-ML</span><span>PART-M/G</span><span>PART-CAMO</span><span>PART-CAO</span
        ><span>PART-145</span><span>14 CFR 91.409</span>
      </div>
    </div>
    <div style="font-family:var(--mono);font-size:10.5px;letter-spacing:.1em;color:rgba(255,255,255,.5)">
      FLYPACK CAMO · CONTINUING AIRWORTHINESS MANAGEMENT
    </div>
  </div>
`;

const shell = (body: string): string => html`<div class="auth">${artPanel}
  <div class="auth-form"><div class="auth-box">${body}</div></div>
</div>`;

const fieldError = (id: string) =>
  html`<span class="hint" id="${id}" style="color:var(--exceed);display:none"></span>`;

function showError(box: HTMLElement, message: string) {
  const el = box.querySelector<HTMLElement>('#form-error');
  if (!el) return;
  el.textContent = message;
  el.style.display = 'block';
}

function clearError(box: HTMLElement) {
  const el = box.querySelector<HTMLElement>('#form-error');
  if (el) el.style.display = 'none';
}

function busy(btn: HTMLButtonElement, on: boolean, idle: string) {
  btn.disabled = on;
  btn.textContent = on ? 'Working…' : idle;
}

// ── sign in ─────────────────────────────────────────────────────────────────

export function loginView(params: URLSearchParams): string {
  const oauthError = params.get('error');
  return shell(html`
    <div>
      <h1>Sign in</h1>
      <p class="muted tiny" style="margin-top:4px">Continuing airworthiness management</p>
    </div>

    ${oauthError
      ? html`<div class="callout c-exceed"><span class="cg">✕</span><div>${esc(oauthError)}</div></div>`
      : ''}

    <a class="gbtn" href="/api/auth/google" data-external>${GOOGLE_G} Continue with Google</a>

    <div class="sep tiny lbl">or</div>

    <form class="form" id="login-form" novalidate>
      <div class="field">
        <label for="email">Work email</label>
        <input id="email" name="email" type="email" autocomplete="username" required />
      </div>
      <div class="field">
        <label for="password">Password</label>
        <input id="password" name="password" type="password" autocomplete="current-password" required />
      </div>
      ${fieldError('form-error')}
      <div style="display:flex;justify-content:flex-end">
        <a class="tiny" href="/forgot-password">Forgot password?</a>
      </div>
      <button class="btn pri wide" type="submit" id="submit">Sign in</button>
    </form>

    <div class="demo">
      <div style="flex:1">
        <div class="lbl">Demo fleet</div>
        <p class="tiny muted" style="margin-top:3px">
          Six aircraft, live due list, real data. No sign-up needed.
        </p>
      </div>
      <button class="btn" type="button" id="demo">Open demo</button>
    </div>

    <p class="tiny dim" style="text-align:center">
      New organisation? <a href="/register">Create an organisation account</a>
    </p>
  `);
}

export function bindLogin(root: HTMLElement, onSignedIn: (s: SignInResponse) => void): void {
  const form = root.querySelector<HTMLFormElement>('#login-form')!;
  const submit = root.querySelector<HTMLButtonElement>('#submit')!;
  const demo = root.querySelector<HTMLButtonElement>('#demo')!;

  form.addEventListener('submit', async (e) => {
    e.preventDefault();
    clearError(root);
    busy(submit, true, 'Sign in');
    try {
      const data = new FormData(form);
      onSignedIn(await api.login(String(data.get('email')), String(data.get('password'))));
    } catch (err) {
      showError(root, err instanceof ApiError ? err.message : 'Could not sign in. Try again.');
    } finally {
      busy(submit, false, 'Sign in');
    }
  });

  demo.addEventListener('click', async () => {
    clearError(root);
    busy(demo, true, 'Open demo');
    try {
      onSignedIn(await api.demo());
    } catch (err) {
      showError(root, err instanceof ApiError ? err.message : 'The demo is unavailable.');
    } finally {
      busy(demo, false, 'Open demo');
    }
  });
}

// ── register ────────────────────────────────────────────────────────────────

export function registerView(): string {
  return shell(html`
    <div>
      <h1>Create your account</h1>
      <p class="muted tiny" style="margin-top:4px">
        Step 1 of 2 — your details. Fleet and organisation come next.
      </p>
    </div>

    <a class="gbtn" href="/api/auth/google" data-external>${GOOGLE_G} Sign up with Google</a>

    <div class="sep tiny lbl">or</div>

    <form class="form" id="register-form" novalidate>
      <div class="frow">
        <div class="field">
          <label for="firstName">First name</label>
          <input id="firstName" name="firstName" required />
        </div>
        <div class="field">
          <label for="lastName">Last name</label>
          <input id="lastName" name="lastName" required />
        </div>
      </div>
      <div class="field">
        <label for="email">Work email</label>
        <input id="email" name="email" type="email" autocomplete="username" required />
        <span class="hint">We send a link to confirm the address before the account is active.</span>
      </div>
      <div class="field">
        <label for="password">Password</label>
        <input id="password" name="password" type="password" autocomplete="new-password" required />
        <span class="hint">At least 12 characters, including a number and a symbol.</span>
      </div>
      ${fieldError('form-error')}
      <label class="check tiny">
        <input type="checkbox" id="terms" required />
        I accept the terms of service and the data-processing agreement.
      </label>
      <button class="btn pri wide" type="submit" id="submit">Create account</button>
    </form>

    <p class="tiny dim" style="text-align:center">Already registered? <a href="/login">Sign in</a></p>
  `);
}

export function bindRegister(root: HTMLElement): void {
  const form = root.querySelector<HTMLFormElement>('#register-form')!;
  const submit = root.querySelector<HTMLButtonElement>('#submit')!;

  form.addEventListener('submit', async (e) => {
    e.preventDefault();
    clearError(root);
    if (!root.querySelector<HTMLInputElement>('#terms')!.checked) {
      showError(root, 'Please accept the terms to continue.');
      return;
    }
    busy(submit, true, 'Create account');
    try {
      const d = new FormData(form);
      const r = await api.register({
        email: String(d.get('email')),
        password: String(d.get('password')),
        firstName: String(d.get('firstName')),
        lastName: String(d.get('lastName')),
      });
      // An address that already has an account is not told to check an inbox —
      // in the ordinary case nothing has been sent, and the useful next step
      // is to sign in rather than to wait for mail that is not coming.
      root.innerHTML = r.exists
        ? html`
            <div><h1>You already have an account</h1></div>
            <div class="callout c-caution">
              <span class="cg">!</span>
              <div>${esc(r.message)}<br /><b class="mono">${esc(r.email)}</b></div>
            </div>
            <p class="tiny dim">
              <a href="/login">Sign in</a> ·
              <a href="/forgot-password">Forgot password?</a>
            </p>
          `
        : html`
            <div><h1>Check your inbox</h1></div>
            <div class="callout c-ok">
              <span class="cg">✓</span>
              <div>${esc(r.message)}<br /><b class="mono">${esc(r.email)}</b></div>
            </div>
            <p class="tiny dim">
              Nothing arrived? Look in spam, then <a href="/login">sign in</a> to request another link.
            </p>
          `;
    } catch (err) {
      showError(
        root,
        err instanceof ApiError ? err.fieldMessages.join(' ') || err.message : 'Could not create the account.',
      );
    } finally {
      busy(submit, false, 'Create account');
    }
  });
}

// ── forgot / reset ──────────────────────────────────────────────────────────

export const forgotView = (): string =>
  shell(html`
    <div>
      <h1>Reset your password</h1>
      <p class="muted tiny" style="margin-top:4px">
        We will email a link to set a new one. It is valid for an hour.
      </p>
    </div>
    <form class="form" id="forgot-form" novalidate>
      <div class="field">
        <label for="email">Work email</label>
        <input id="email" name="email" type="email" autocomplete="username" required />
      </div>
      ${fieldError('form-error')}
      <button class="btn pri wide" type="submit" id="submit">Send the link</button>
    </form>
    <p class="tiny dim" style="text-align:center"><a href="/login">Back to sign in</a></p>
  `);

export function bindForgot(root: HTMLElement): void {
  const form = root.querySelector<HTMLFormElement>('#forgot-form')!;
  const submit = root.querySelector<HTMLButtonElement>('#submit')!;
  form.addEventListener('submit', async (e) => {
    e.preventDefault();
    busy(submit, true, 'Send the link');
    try {
      const r = await api.forgotPassword(String(new FormData(form).get('email')));
      root.innerHTML = html`
        <div><h1>Check your inbox</h1></div>
        <div class="callout c-ok"><span class="cg">✓</span><div>${esc(r.message)}</div></div>
        <p class="tiny dim" style="text-align:center"><a href="/login">Back to sign in</a></p>
      `;
    } catch {
      showError(root, 'Could not send the link. Try again shortly.');
    } finally {
      busy(submit, false, 'Send the link');
    }
  });
}

export const resetView = (params: URLSearchParams): string => {
  const token = params.get('token');
  if (!token) {
    return shell(html`
      <div><h1>Link not valid</h1></div>
      <div class="callout c-exceed">
        <span class="cg">✕</span>
        <div>This reset link is incomplete. Request a new one from the sign-in page.</div>
      </div>
      <p class="tiny dim" style="text-align:center"><a href="/forgot-password">Request a new link</a></p>
    `);
  }
  return shell(html`
    <div>
      <h1>Choose a new password</h1>
      <p class="muted tiny" style="margin-top:4px">Signing in elsewhere will be ended.</p>
    </div>
    <form class="form" id="reset-form" novalidate>
      <input type="hidden" id="token" value="${esc(token)}" />
      <div class="field">
        <label for="password">New password</label>
        <input id="password" name="password" type="password" autocomplete="new-password" required />
        <span class="hint">At least 12 characters, including a number and a symbol.</span>
      </div>
      ${fieldError('form-error')}
      <button class="btn pri wide" type="submit" id="submit">Set my password</button>
    </form>
  `);
};

export function bindReset(root: HTMLElement): void {
  const form = root.querySelector<HTMLFormElement>('#reset-form');
  if (!form) return;
  const submit = root.querySelector<HTMLButtonElement>('#submit')!;
  form.addEventListener('submit', async (e) => {
    e.preventDefault();
    busy(submit, true, 'Set my password');
    try {
      const token = root.querySelector<HTMLInputElement>('#token')!.value;
      const password = String(new FormData(form).get('password'));
      await api.resetPassword(token, password);
      toast('Password set. Sign in with it now.');
      location.assign('/login');
    } catch (err) {
      showError(
        root,
        err instanceof ApiError ? err.fieldMessages.join(' ') || err.message : 'Could not set the password.',
      );
    } finally {
      busy(submit, false, 'Set my password');
    }
  });
}

// ── email verification ──────────────────────────────────────────────────────

export const verifyView = (): string =>
  shell(html`<div><h1>Confirming your address</h1></div>${''}
    <div class="callout c-info" id="verify-state">
      <span class="cg">·</span><div>One moment…</div>
    </div>`);

export async function bindVerify(root: HTMLElement, params: URLSearchParams): Promise<void> {
  const box = root.querySelector<HTMLElement>('#verify-state')!;
  const token = params.get('token');
  if (!token) {
    box.className = 'callout c-exceed';
    box.innerHTML = html`<span class="cg">✕</span><div>This confirmation link is incomplete.</div>`;
    return;
  }
  try {
    const r = await api.verifyEmail(token);
    box.className = 'callout c-ok';
    box.innerHTML = html`<span class="cg">✓</span>
      <div>${esc(r.message)}<p style="margin-top:10px"><a class="btn sm" href="/login">Sign in</a></p></div>`;
  } catch (err) {
    box.className = 'callout c-exceed';
    box.innerHTML = html`<span class="cg">✕</span>
      <div>${esc(err instanceof ApiError ? err.message : 'That link is no longer valid.')}</div>`;
  }
}

// ── organisation picker ─────────────────────────────────────────────────────

export const chooseOrgView = (session: SignInResponse): string =>
  shell(html`
    <div>
      <h1>Choose an organisation</h1>
      <p class="muted tiny" style="margin-top:4px">
        Your account has access to more than one. You can switch at any time.
      </p>
    </div>
    <div class="pick">
      ${session.organisations
        .map(
          (o) => html`
            <label>
              <input type="radio" name="org" value="${esc(o.id)}" />
              <span class="pb">
                <span class="pt">${esc(o.name)}</span>
                <span class="pm">
                  ${/* A copy read back from a backup, said before it is opened rather
                        than after — an old record set that looks live is worse than
                        no record set at all. */ ''}
                  ${o.restoredAt ? 'Copy of a backup · ' : ''}${esc(o.approvalReference ?? '—')} · ${esc(o.role)}
                </span>
              </span>
            </label>
          `,
        )
        .join('')}
    </div>
    <button class="btn pri wide" type="button" id="continue" disabled>Continue</button>
  `);

export function bindChooseOrg(root: HTMLElement, onChosen: (s: SignInResponse) => void): void {
  const cont = root.querySelector<HTMLButtonElement>('#continue')!;
  root.querySelectorAll<HTMLInputElement>('input[name=org]').forEach((r) =>
    r.addEventListener('change', () => (cont.disabled = false)),
  );
  cont.addEventListener('click', async () => {
    const chosen = root.querySelector<HTMLInputElement>('input[name=org]:checked');
    if (!chosen) return;
    busy(cont, true, 'Continue');
    try {
      onChosen(await api.selectOrganisation(chosen.value));
    } catch {
      showError(root, 'Could not open that organisation.');
    } finally {
      busy(cont, false, 'Continue');
    }
  });
}

// ── first organisation ──────────────────────────────────────────────────────

/**
 * Where sign-up actually ends.
 *
 * An account with no organisation cannot be issued a scoped access token, so
 * every screen behind this one would answer 401. Rendering the app shell for
 * such an account — which is what used to happen — produces an application
 * that looks signed in and works at nothing.
 */
export const createOrgView = (session: SignInResponse): string =>
  shell(html`
    <div>
      <h1>Set up your organisation</h1>
      <p class="muted tiny" style="margin-top:4px">
        Signed in as ${esc(session.user.email)}. Everything in FlyPack CAMO — aircraft, programmes,
        people, records — belongs to an organisation, so there is one more step.
      </p>
    </div>

    <div class="field">
      <label for="o-name">Organisation name</label>
      <input id="o-name" placeholder="Aeroclub Băneasa" maxlength="200" />
    </div>

    <div class="field">
      <label for="o-kind">Approval held</label>
      <select id="o-kind">
        <option value="CAO">Part-CAO — combined airworthiness organisation</option>
        <option value="CAMO">Part-CAMO</option>
        <option value="M_SUBPART_G">Part-M Subpart G</option>
      </select>
      <span class="hint">You can change this later, and add more approvals under Organisation info.</span>
    </div>

    <div class="field">
      <label for="o-ref">Approval reference</label>
      <input id="o-ref" placeholder="CAO.RO.0042" maxlength="40" style="font-family:var(--mono)" />
      <span class="hint">Leave blank if the approval has not been issued yet.</span>
    </div>

    <div class="field">
      <label for="o-authority">Competent authority</label>
      <input id="o-authority" placeholder="AACR" maxlength="80" />
    </div>

    <button class="btn pri wide" type="button" id="create-org">Create organisation</button>
  `);

export function bindCreateOrg(root: HTMLElement, onCreated: (s: SignInResponse) => void): void {
  const btn = root.querySelector<HTMLButtonElement>('#create-org')!;
  const value = (id: string) => root.querySelector<HTMLInputElement | HTMLSelectElement>(id)!.value.trim();

  btn.addEventListener('click', async () => {
    const name = value('#o-name');
    if (!name) {
      showError(root, 'Give the organisation a name.');
      return;
    }
    busy(btn, true, 'Create organisation');
    try {
      const created = await api.createOrganisation({
        name,
        approvalKind: value('#o-kind'),
        approvalReference: value('#o-ref') || undefined,
        competentAuthority: value('#o-authority') || undefined,
      });

      // It exists — it simply cannot be opened until the address is confirmed.
      // Handing this to onCreated would try to start a session that has no
      // access token, and the organisation would look like it had failed.
      if (created.needsVerification) {
        root.innerHTML = html`
          <div><h1>Check your inbox</h1></div>
          <div class="callout c-ok">
            <span class="cg">✓</span>
            <div>${esc(created.message ?? 'Confirm your email address to open the organisation.')}</div>
          </div>
          <p class="tiny dim">
            Nothing arrived? Look in spam. The link is good for 24 hours.
          </p>
        `;
        return;
      }

      onCreated(created);
    } catch (err) {
      showError(root, err instanceof ApiError ? err.message : 'Could not create the organisation.');
    } finally {
      busy(btn, false, 'Create organisation');
    }
  });
}
